import type { IconComponent } from "@overmap-ai/blocks"
import { ReportStatus } from "@overmap-ai/common"
import { amber, gray, green, red } from "@radix-ui/colors"
import { CircleCheck, CircleX, Clock, LoaderCircle } from "lucide-react"

import type {
	AssetPdfExportOptions,
	IssuePdfExportOptions,
	FormSubmissionPdfExportOptions,
	ProcedurePdfExportOptions,
} from "../typings/reports"

export const reportStatusData: Record<ReportStatus, { icon: IconComponent; label: string; color: string }> = {
	[ReportStatus.PENDING]: { icon: Clock, label: "Pending", color: gray.gray9 },
	[ReportStatus.GENERATING]: { icon: LoaderCircle, label: "Generating", color: amber.amber9 },
	[ReportStatus.FAILED]: { icon: CircleX, label: "Failed", color: red.red9 },
	[ReportStatus.COMPLETE]: { icon: CircleCheck, label: "Complete", color: green.green9 },
}

export const assetPdfExportOptionsData: Record<keyof AssetPdfExportOptions, { label: string; description: string }> = {
	asset_type_identifiers: {
		label: "Identifiers",
		description: "Include asset identifiers and their values.",
	},
	asset_type_fields: {
		label: "Field values",
		description: "Include asset fields and their values.",
	},
	procedures: {
		label: "Procedures",
		description: "Include procedures associated with the asset.",
	},
	issue_associations: {
		label: "Issues",
		description: "Include issues linked to the asset.",
	},
	form_submissions: {
		label: "Form submissions",
		description: "Include form submissions linked to the asset.",
	},
	asset_attachments: {
		label: "Attachments",
		description: "Include files attached to the asset.",
	},
	asset_comments: {
		label: "Comments",
		description: "Include comments on the asset.",
	},
}

export const issuePdfExportOptionsData: Record<keyof IssuePdfExportOptions, { label: string; description: string }> = {
	issue_type_identifiers: {
		label: "Identifiers",
		description: "Include issue identifiers and their values.",
	},
	issue_type_fields: {
		label: "Field values",
		description: "Include issue fields and their values.",
	},
	issue_associations: {
		label: "Linked assets",
		description: "Include assets linked to the issue.",
	},
	form_submissions: {
		label: "Form submissions",
		description: "Include form submissions linked to the issue.",
	},
	issue_attachments: {
		label: "Attachments",
		description: "Include files attached to the issue.",
	},
	issue_comments: {
		label: "Comments",
		description: "Include comments on the issue.",
	},
}

export const formSubmissionPdfExportOptionsData: Record<
	keyof FormSubmissionPdfExportOptions,
	{ label: string; description: string }
> = {
	form_identifiers: {
		label: "Identifiers",
		description: "Include form identifiers and their values.",
	},
	form_fields: {
		label: "Field values",
		description: "Include form fields and their submitted values.",
	},
	form_submission_comments: {
		label: "Comments",
		description: "Include comments on the form submission.",
	},
}

export const procedurePdfExportOptionsData: Record<
	keyof ProcedurePdfExportOptions,
	{ label: string; description: string }
> = {
	procedure_steps: {
		label: "Steps",
		description: "Include procedure steps and their completion details.",
	},
	procedure_type_fields: {
		label: "Field values",
		description: "Include procedure fields and their values.",
	},
}
